import _ from "lodash";
import { TerritoryPointMapping } from "./TerritoryPointMapping";
import TerritorySelectionModel from "./TerritorySelectionModel";
import {TerritoryRelatedFeaturesMapping} from "@esWebAPI/es/map/TerritoryRelatedFeaturesMapping";

import type {Criteria, EqualCriteria, InCriteria} from "@esWebAPI/es/model/Criteria";
import type {FeatureViewModel} from "@esWebAPI/es/map/FeatureViewModel";
import type {IEventChannel} from "@esWebAPI/es/model/Models";
import type LayerViewModel from "@esWebAPI/es/map/LayerViewModel";

export type RelatedLayerViewMapping = {
    baseLayerView: {
        [lvId: number]: number
    },
    extraRelatedPointLayerView: {
        [lvId: number]: number
    },
    relatedLayerView: {
        [lvId: number]: number
    },
}

export default class TerritoryModel{

    readonly featureViewModel: FeatureViewModel;
    readonly eventChannel: IEventChannel;
    readonly loadComplete;
    readonly pointMapping: TerritoryPointMapping;
    readonly selectionModel: TerritorySelectionModel;
    readonly _this = this;

    private relatedLayerViewMapping: RelatedLayerViewMapping|undefined;
    private relatedFeaturesMapping: TerritoryRelatedFeaturesMapping|undefined;

    currentTerritoryId = -1;
    nextTerritoryId = 1;

    // array of territory obj
    territoryFeatures : Array<ITerritoryFeature> = [];
    changedTerritoriesIds: number [] = [];

    territoryMeta: any;
    baseLayerConfig: any;
    baseLayerFsId = -1;

    mainLayerView: LayerViewModel;
    baseLayerView: LayerViewModel;

    currentLevel = 1;
    parent:any;

    private unassignedTerritory : ITerritoryFeature;
    private unassignedTerritoryHierarchy : ITerritoryFeature[] = [];
    private allBaseFeatureIds: number[] = [];

    filteredInFeatureIds: {[key: number]: number[]} = {};

    constructor(featureViewModel: FeatureViewModel, eventChannel: IEventChannel){
        this.featureViewModel = featureViewModel;
        this.eventChannel = eventChannel;
        this.pointMapping = new TerritoryPointMapping(featureViewModel, this, eventChannel);
        this.selectionModel = new TerritorySelectionModel(this, this.pointMapping, eventChannel);

        const geoMapId = this.featureViewModel.geoMapId;

        this.loadComplete = $.when(
            isTerritoryMap(geoMapId),
            this.featureViewModel.onReady
        ).then((isTerritory) => {
            if (!isTerritory) {
                this.mainLayerView = this.featureViewModel.getLayersByType("TerritoryLayerView")[0];
                return es.util.deferred.resolved();
            }

            this.mainLayerView = this.featureViewModel.getLayersByType("ManageTerritoryMainLayerView")[0];
            this.baseLayerView = this.featureViewModel.getLayersByType("ManageTerritoryBaseLayerView")[0];

            this.unassignedTerritory = this.createUnassignedTerritory();

            return this.initMeta(geoMapId);
        });

        async function isTerritoryMap(geoMapId: number) {
           const geoMap = await es.api.geoMap.get(geoMapId);
           return !!geoMap.territoryMetaId;
        }
    }

    // Note: the first 12 colors/territories will have complimentary colors but after that it's random. Find better solution
    getNewTerritoryColor (index?: number) {
        // if resetRanges has been called then continue using that palette but it must be a colour that hasn't already been used.
        const territoryColors = es.util.color.palettes["Vivid"];
        if(index !== null && index !== undefined && index < territoryColors.length) {
            return es.util.color.fromHex(territoryColors[index]);
        } else if (this.hasTerritory() && this.territoryFeatures.length < territoryColors.length) {
            return es.util.color.fromHex(territoryColors[this.territoryFeatures.length]);
        } else {
            return es.util.color.getRandomColor();
        }
    }

    getFeatureAttrs (feature: any) { // FIXME :: Feature interface
        if (feature.tags)
            return feature.tags;
        if (feature.attrs)
            return feature.attrs;
        if (feature.properties)
            return feature.properties;
        return [];
    }

    reset () {
        this.nextTerritoryId = 1;
        this.territoryFeatures = [];
        this.pointMapping.clear();
    }

    getUnassignedTerritory () {
        return this.unassignedTerritory;
    }

    getAdditionalHierarchicalUnassignedTerritories (){
        return this.unassignedTerritoryHierarchy;
    }

    load(territoryFeaturesRs: ITerritoryFeature[], newCurrentTerritoryId: number, suppressEvents = false) {
        const isSameCurrentTerritoryId = this.currentTerritoryId === newCurrentTerritoryId;

        this.nextTerritoryId = 1;
        this.territoryFeatures = [];
        this.changedTerritoriesIds = [];

        this.unassignedTerritory = territoryFeaturesRs.find(tf => tf.ES_ID === -1) ?? this.unassignedTerritory;

        if (this.allBaseFeatureIds && this.unassignedTerritory.ES_BASE_FEATURE_IDS !== this.allBaseFeatureIds) {
            this.unassignedTerritory.ES_BASE_FEATURE_IDS = es.util.object.deepCopy(this.allBaseFeatureIds);
        }

        const isAccountOnly = this.isAccountOnly();
        const unassignedBaseFeatureIdsSet = new Set(this.unassignedTerritory.ES_BASE_FEATURE_IDS);
        for (const territoryFeature of territoryFeaturesRs) {

            if (territoryFeature.ES_ID <= 0) {
                continue;
            }

            if (!isAccountOnly) {
                territoryFeature.ES_BASE_FEATURE_IDS.sort((a, b) => a - b);
                territoryFeature.ES_BASE_FEATURE_IDS.forEach(id => unassignedBaseFeatureIdsSet.delete(id));

                if (territoryFeature.hasOwnProperty('ES_BASE_POINT_IDS')) {
                    territoryFeature.ES_BASE_POINT_IDS.sort((a, b) => a - b);
                }
            }

            if(territoryFeature.ES_EXCEPTIONS.length > 0){
               for(const exByLm of territoryFeature.ES_EXCEPTIONS){
                   exByLm.featureIds.sort((a, b) => a - b);
               }
            }

            this.create(territoryFeature, suppressEvents);
        }

        this.unassignedTerritory.ES_BASE_FEATURE_IDS = Array.from(unassignedBaseFeatureIdsSet).sort((a, b) => a - b);

        if (isSameCurrentTerritoryId) {
            this.currentTerritoryId = newCurrentTerritoryId;
        }

        // Ensure that the territories are in order
        this.territoryFeatures.sort((a, b) => a.ES_ID - b.ES_ID);
        this.setCurrentTerritoryById(newCurrentTerritoryId);
    }


    reload (territoryFeaturesRs: ITerritoryFeature[], currentTerritoryId: number) {
        this.load(territoryFeaturesRs, currentTerritoryId, true);
        this.eventChannel.fireEvent("onTerritoryChange", {"RELOAD": true}, territoryFeaturesRs);
    }

    reloadFromServer () {
        return es.api.territory.loadAll(this.mainLayerView.getId()).then((territoryFeaturesRs: ITerritoryFeature[]) =>{
            this.reload(territoryFeaturesRs, this.currentTerritoryId);
        });
    }

    async undo(territoryFeatures: ITerritoryFeature[], filteredInTerritories: number[]) {
        return es.api.territory.loadFromJson(this.mainLayerView.getId(), territoryFeatures).then(async (territoryFeaturesRs: ITerritoryFeature[]) => {
            this.load(territoryFeaturesRs, this.currentTerritoryId, true);
            await this.setFilter(filteredInTerritories, true);
            this.resetChangedTerritoriesIds();
            for (const t of territoryFeaturesRs) {
                for (const da of this.territoryMeta.territoryDerivedAttributes) {
                    if (!t.hasOwnProperty(da.name)) {
                        this.updateChangedTerritoriesIds(t.ES_ID);
                        break;
                    }
                }
            }
            this.eventChannel.fireEvent("onTerritoryChange", {"UNDO": true}, territoryFeaturesRs);
        });
    }

    async redo (territoryFeatures: ITerritoryFeature[], filteredInTerritories: number[]) {
        return es.api.territory.loadFromJson(this.mainLayerView.getId(), territoryFeatures).then(async (territoryFeaturesRs: ITerritoryFeature[]) => {
            this.load(territoryFeaturesRs, this.currentTerritoryId, true);
            await this.setFilter(filteredInTerritories, true);
            this.eventChannel.fireEvent("onTerritoryChange", {"REDO": true}, territoryFeaturesRs);
        });
    }

    /**
     * Strips out leading chars +, -, = or @
     * See: TOR9-14429
     * @param name
     * @returns clean and valid territory name.
     */
    private getValidTerritoryName (name: string) : string{
        if( name.startsWith("+") ||
            name.startsWith("-") ||
            name.startsWith("=") ||
            name.startsWith("@") ||
            name.startsWith(" ") ){

            return this.getValidTerritoryName(name.substring(1));
        }
        return name;
    }

    public getTerritoryLevelLabel (level:number, replace: boolean) {

        if(!this.territoryMeta.levelLabels) {
            return "Territories";
        }

        const label = this.territoryMeta['levelLabels'][level - 1];
        if (replace) {
            return label.replace(/^(Region|District)s$/, '$1').replace(/^(Territor)ies$/, '$1y');
        }
        return label;
    }

    /**
     * Create single territory feature model (e.g. 'west coast'
     *  {
     *      "ES_STYLE_COLOR": 345426,
     *      "ES_BASE_FEATURE_IDS": [22, 23, 35],
     *      "NAME": "west coast",
     *      "ES_ID": 2
     *  }
     */
    create (territory: ITerritoryFeature, isSuppressEvents: boolean){

        //const newTer = es.util.object.deepCopy(territory);
        const newTer = territory;

        newTer.NAME = this.getValidTerritoryName(newTer.NAME);

        if (!newTer["ES_STYLE_COLOR"]){
            newTer.ES_STYLE_COLOR = this.getNewTerritoryColor();
        } else {
            newTer.ES_STYLE_COLOR = es.util.color.withTransparency(newTer.ES_STYLE_COLOR, 0); // force 0% transparency
        }

        this.territoryFeatures.push(newTer);
        this.currentTerritoryId = this.territoryFeatures[this.territoryFeatures.length - 1].ES_ID;
        this.nextTerritoryId = Math.max(this.nextTerritoryId, this.currentTerritoryId + 1);

        if (!isSuppressEvents) {
            this.eventChannel.fireEvent("onTerritoryChange", {"ADD_TERRITORY": true}, [newTer]);
            this.eventChannel.fireEvent("onTerritoryCurrentChange", this.currentTerritoryId);
        }

        return this.getCurrentTerritoryObjRef();
    }

    async createBlank(territoryName: string, isSuppressEvents: boolean) {

        if (this.isHierarchical() && this.currentLevel > 1 && !parent) {
            return;
        }

        const esId = this.nextTerritoryId++;
        if (!!territoryName || (territoryName === "")) {
            if (this.isHierarchical()) {
                const label = this.getTerritoryLevelLabel(this.currentLevel, true);
                territoryName = label + " " + esId;
            } else {
                territoryName = "Territory " + esId;
            }
        }
        let blank = {
            "ES_STYLE_COLOR": this.getNewTerritoryColor(),
            "ES_EXCEPTIONS": [],
            "NAME": territoryName,
            "ES_ID": esId
        } as unknown as ITerritoryFeature;

        // add derived attributes too
        const derivedAttributes = this.getTerritoryMetaObjRef().territoryDerivedAttributes;
        for (let i = 0; i < derivedAttributes.length; i++) {
            if (derivedAttributes[i].name) {
                blank[derivedAttributes[i].name] = 0;
            }
        }

        if (!this.isAccountOnly()) {
            blank.ES_BASE_FEATURE_IDS = [];
            if (this.hasRelatedExtraPointLayer()) {
                blank.ES_BASE_POINT_IDS = [];
            }
        } else {
            blank.ES_LOCKED_EXCEPTIONS = []
        }


        // Set level and parent for hierarchical
        if (this.isHierarchical()) {
            if (this.getCurrentParent()) {
                blank.ES_PARENT_ID = this.getCurrentParent();
                blank.ES_LEVEL = this.get(this.getCurrentParent()).ES_LEVEL + 1;
            } else {
                blank.ES_LEVEL = 1;
            }
        }
        blank = this.create(blank, isSuppressEvents);

        let territoryFilter = this.getFilter();
        if (territoryFilter) {
            const idsToAdd = this.getAncestors(blank.ES_ID).concat(blank.ES_ID).filter(id => !territoryFilter?.includes(id));
            territoryFilter = territoryFilter.concat(idsToAdd);
            territoryFilter.sort();
            
            await this.setFilter(territoryFilter);
        }

        return blank;
    }

    createBlankHierarchy (isSuppressEvents: boolean) {
        const levels = this.territoryMeta['levels'];
        let parentId = undefined;
        let lastEsId = -1;

        for (let i = 1; i <= levels; i++) {
            const esId = this.nextTerritoryId++;

            const label = this.getTerritoryLevelLabel(i, true);

            const blank = {
                "ES_STYLE_COLOR": this.getNewTerritoryColor(),
                "ES_EXCEPTIONS": [],
                "NAME": label + " " + esId,
                "ES_ID": esId,
                "ES_LEVEL": i
            } as unknown as ITerritoryFeature;

            if (!this.isAccountOnly()) {
                blank.ES_BASE_FEATURE_IDS = [];
                if (this.hasRelatedExtraPointLayer()) {
                    blank.ES_BASE_POINT_IDS = [];
                }
            } else {
                blank.ES_LOCKED_EXCEPTIONS = []
            }

            if (parentId) {
                blank.ES_PARENT_ID = parentId
            }

            parentId = esId;
            lastEsId = esId;

            this.create(blank, isSuppressEvents);
        }

        this.loadComplete.then(() => {
            this.setCurrentLevel(levels)
            this.setCurrentTerritoryById(lastEsId);
        });
    }

    /**
     * Get ter feat by es_id.
     *
     * @param territoryId
     * @returns {*}
     */
    get(territoryId: number): ITerritoryFeature{
        // note: I've not been able to get territoryFeatures into a state where it was _not_ ordered by id (see the
        // debug #get method commented out above) so the bin search does always find the result. We should change
        // this method to throw/log an err so that we can confidently remove the "backup" linear search.
        return es.util.array.binarySearchGet(this.territoryFeatures, "ES_ID", territoryId) ||
            this.territoryFeatures.find(t => t.ES_ID == territoryId);
    }

//    getByIds (ids: number[]) {
//        return this.territoryFeatures.filter(t => ids.includes(t.ES_ID));
//    }

    getByIds(ids: number[]) {
        const idSet = new Set(ids);
        return this.territoryFeatures.filter(t => idSet.has(t.ES_ID));
    }

    async removeTerritory (territoryId: number, suppressEvents = false) {
        const childTers = this.getByParentId(territoryId, false);
        const removedTerritories: ITerritoryFeature[] = [];
        if(childTers) {
            for(const childTer of childTers) {
                removedTerritories.push(...await this.removeTerritory(childTer.ES_ID, true));
            }
        }

        const territoryFilter = this.getFilter();
        if (territoryFilter && territoryFilter.length){
            _.remove(territoryFilter, function(territory: number){
                return territory === territoryId;
            });
            await this.setFilter(territoryFilter.length ? territoryFilter : null);
        }

        const territory = this.get(territoryId);
        const index = this.territoryFeatures.findIndex(t => t.ES_ID == territoryId);
        if(index > -1){
            this.currentTerritoryId = -1;
            this.territoryFeatures.splice(index, 1);
            removedTerritories.push(territory);
        }

        if (removedTerritories.length && !suppressEvents) {
            this.eventChannel.fireEvent("onTerritoryChange", {"REMOVE_TERRITORY": true}, removedTerritories);
        }

        return removedTerritories;
    }

    /*
     *  -------------------------------------------------------------------------------------------------------------
     *  Assignment private methods
     *  -------------------------------------------------------------------------------------------------------------
     */

    /**
     * if baseFeatIds are string convert them to number. This happens if the feats are fetched from terraformer.
     * @param ids
     * @returns {*}
     */
    private convertStringIdsToInt (ids: string[] | number[]) : number[]{
        if(typeof(ids[0]) == "string"){
            for(let i = 0; i < ids.length; i++){
                ids[i] = parseInt(ids[i] as string);
            }
        }
        return ids as number[];
    }

    private concatExceptions (exceptionsA: ITerritoryException[], exceptionsB: ITerritoryException[]){
        if(!exceptionsA && !exceptionsB){
            return [];
        }

        if(!exceptionsA) {
            exceptionsA = [];
        }
        if(!exceptionsB) {
            exceptionsB = [];
        }

        const result = es.util.object.deepCopy(exceptionsA);
        for(const exB of exceptionsB) {
            if (!result.get("layerMetaId", exB.layerMetaId)) {
                result.push({
                    layerMetaId: exB.layerMetaId,
                    featureIds: es.util.object.deepCopy(exB.featureIds)
                });
            } else {
                let exceptionsAFeatureIds = result.get("layerMetaId", exB.layerMetaId).featureIds;
                exceptionsAFeatureIds = _.uniq(_.union(exceptionsAFeatureIds, exB.featureIds));
                result.get("layerMetaId", exB.layerMetaId).featureIds = exceptionsAFeatureIds;
            }
        }
        return result;
    }

    getSelectionModel () {
        return this.selectionModel;
    }

    /**
     * Assign given feature ids to currentTerritory, removing duplicates if necessary
     *
     * @param idProp one of ES_BASE_FEATURE_IDS or ES_BASE_POINT_IDS
     * @param newBaseFeats
     * @param isSuppressEvents if true does not fire events. useful when a series of assignments by geom are being performed and we do not want to re-render until all are complete.

     * @param territory If set, the features will be assigned to this territory
     *
     * @return boolean Returns True if the feature have been assigned
     */
    private assign(
        idProp: "ES_BASE_FEATURE_IDS" | "ES_BASE_POINT_IDS",
        newBaseFeats: any,
        isSuppressEvents: boolean,
        territory: ITerritoryFeature
    ) {
        let newBaseFeatIds = this.convertStringIdsToInt(newBaseFeats.map(f => f.id));

        for(const terFeat of this.territoryFeatures) {
            // Feature from child territories must not be stolen
            if (terFeat.ES_PARENT_ID === territory.ES_ID) {
                newBaseFeatIds = newBaseFeatIds.filter(item => {
                    return !terFeat[idProp].includes(item);
                });
            } else {
                // remove any ids from other territories and leave in newBaseFeatIds
                terFeat[idProp] = terFeat[idProp].filter((item: any) => {
                    if (newBaseFeatIds.includes(item)) {
                        this.updateChangedTerritoriesIds(terFeat.ES_ID);
                    }
                    return !newBaseFeatIds.includes(item);
                });
            }
        }

        for (const terFeat of this.territoryFeatures) {
            terFeat[idProp].sort((a, b) => a - b);
        }

        this.updateChangedTerritoriesIds(territory.ES_ID);

        territory[idProp] = _.uniq(territory[idProp].concat(newBaseFeatIds));
        territory[idProp].sort((a, b) => a - b);
        //territory[property] = es.util.object.quicksort(territory[property]);

        if(!isSuppressEvents) {
            const isPointFeatures = idProp === "ES_BASE_POINT_IDS";
            const eventData: ITerritoryActions = {};
            eventData.ADD_FEATS = {baseFeatureIds: [], basePointIds: []};

            if (isPointFeatures) {
                eventData.ADD_FEATS.basePointIds = newBaseFeatIds;
            } else {
                eventData.ADD_FEATS.baseFeatureIds = newBaseFeatIds;
            }

            this.eventChannel.fireEvent("onTerritoryChange", eventData, [territory], this.changedTerritoriesIds);
        }

        if(!this.isBottomLevel(territory.ES_LEVEL)){
            console.warn("assign feats at territory not at bottom level", territory, newBaseFeatIds);
        }

        if(idProp == "ES_BASE_FEATURE_IDS"){
            this.unassignedTerritory.ES_BASE_FEATURE_IDS = _.difference(this.unassignedTerritory.ES_BASE_FEATURE_IDS, newBaseFeatIds)
                .sort((a, b) => a - b);
        }

        return true;
    }

    private unAssign (property:"ES_BASE_FEATURE_IDS" | "ES_BASE_POINT_IDS", features:any[], isSuppressEvents:boolean, territory: ITerritoryFeature) {

        const unAssignedFeatures: any = [];
        for (const territoryFeat of this.territoryFeatures) {
            if ((territory["ES_LEVEL"] === undefined) || (territory.ES_LEVEL === territoryFeat.ES_LEVEL)) {
                // remove any ids from newBaseFeatIds that are already in other territories
                for (const feat of features) {
                    if (territoryFeat[property].includes(feat.id)) {
                        territoryFeat[property] = territoryFeat[property].filter((item: any) => {
                            if (property === "ES_BASE_POINT_IDS") {
                                // If the feature is a point un assign it only if it belong to the specified territory
                                if (territory.ES_ID !== territoryFeat.ES_ID || feat.id != item) {
                                    return true;
                                }
                            }
                            if (feat.id != item) {
                                return true;
                            }

                            if (!_.find(unAssignedFeatures, ["id", item])) {
                                unAssignedFeatures.push(feat);
                            }
                        });
                    }
                }
            }

        }
        this.updateChangedTerritoriesIds(territory.ES_ID);

        if(!isSuppressEvents) {
            const isPointFeatures = property === "ES_BASE_POINT_IDS";
            const eventData : ITerritoryActions = {};
            eventData.REMOVE_FEATS = {basePointIds: [], baseFeatureIds: []};

            if (isPointFeatures) {
                eventData.REMOVE_FEATS.basePointIds = features.map(f => f.id);
            } else {
                eventData.REMOVE_FEATS.baseFeatureIds = features.map(f => f.id);
            }
            this.eventChannel.fireEvent("onTerritoryChange", eventData, [territory],  this.changedTerritoriesIds);
        }

        if(property == "ES_BASE_FEATURE_IDS"){
            const featureIds = features.map(f => f.id);
            this.unassignedTerritory.ES_BASE_FEATURE_IDS = _.union(this.unassignedTerritory.ES_BASE_FEATURE_IDS, featureIds).sort((a, b) => a - b);
        }
        return true;
    }

    private unAssignPolygons (features: any[], isSuppressEvents: boolean, territory: ITerritoryFeature) {
        return this.unAssign("ES_BASE_FEATURE_IDS", features, isSuppressEvents, territory);
    }

    private unAssignPoints (features: any[], isSuppressEvents: boolean, territory: ITerritoryFeature) {
        return this.unAssign("ES_BASE_POINT_IDS", features, isSuppressEvents, territory);
    }

    assignExceptions (exceptions: ITerritoryException[], isSuppressEvents:boolean, territory: ITerritoryFeature) {
        if (!exceptions || exceptions.length === 0) {
            return;
        }

        this.unAssignExceptions(exceptions, true);

        for (const exception of exceptions) {
            const { layerMetaId, featureIds } = exception;

            let exceptionsForLayerMetaId = territory.ES_EXCEPTIONS.find(e => e.layerMetaId === layerMetaId);
            this.updateChangedTerritoriesIds(territory.ES_ID);

            if (!exceptionsForLayerMetaId) {
                territory.ES_EXCEPTIONS.push({ layerMetaId, featureIds: [] });
                exceptionsForLayerMetaId = territory.ES_EXCEPTIONS[territory.ES_EXCEPTIONS.length - 1];
            }

            const uniqueFeatureIds = featureIds.filter(id => !exceptionsForLayerMetaId.featureIds.includes(id));

            exceptionsForLayerMetaId.featureIds.push(...uniqueFeatureIds);
            exceptionsForLayerMetaId.featureIds.sort((a, b) => a - b);
        }

        if(isSuppressEvents !== true && exceptions.length > 0){
            const eventData: ITerritoryActions = {};
            eventData.ADD_EXCEPTIONS = {};
            eventData.ADD_EXCEPTIONS[territory.ES_ID] = exceptions;
            this.eventChannel.fireEvent("onTerritoryChange", eventData, [territory], this.changedTerritoriesIds);
        }
        return es.util.object.deepCopy(exceptions);
    }

    /**
     *
     * @param territoryId
     * @returns Existing Territory or UnassignedTerritory if requesting -1 and using an Areas_And_Accounts territory
     */
    getExtended (territoryId: number) {
        const territoryNId = +territoryId;
        if (!this.isAccountOnly() && territoryNId === -1) {
            return this.getUnassignedTerritory();
        }

        return this.get(territoryNId);
    }

    assignExceptionsInBulk (exceptionsToAddPerTerritoryId: {[key:number] : ITerritoryException[]}) {

       this.resetChangedTerritoriesIds();
       const addedExceptionsPerTerritoryId : {[key: number] : ITerritoryException[]} = {};

        for (const [territoryId, exceptions] of Object.entries(exceptionsToAddPerTerritoryId)) {
            addedExceptionsPerTerritoryId[+territoryId] = this.assignExceptions(exceptions, true, this.getExtended(+territoryId) as ITerritoryFeature);
        }

        return addedExceptionsPerTerritoryId;
    }

    unAssignExceptions (exceptions: ITerritoryException[], isSuppressEvents: boolean = false){

        if (!exceptions || exceptions.length === 0) {
            return;
        }

        for(const exception of exceptions){
            const layerMetaId = exception.layerMetaId;
            const featureIds = exception.featureIds;

            for(const featureId of featureIds) {

                let isException = false;
                let territoryId = null;

                // We use this ?. safe guard to be able to resolve the cases where an exceptions account is no longer in the TerritoryPointMapping.
                // This can happen if the lead is deleted from SalesForce and we do a sync.
                const node = this.pointMapping.getNode(layerMetaId, featureId);
                if(!node) {
                    territoryId = this.getTerritoryIdByException(layerMetaId, featureId);
                    isException = true;
                } else {
                    territoryId = node.territoryId;
                    isException = node.isException;
                }


                if(isException && territoryId) {
                    // remove exception from the territory
                    this.updateChangedTerritoriesIds(territoryId);
                    const territoryToRemoveException = this.getExtended(territoryId) as ITerritoryFeature;
                    if(!territoryToRemoveException) {
                        //TOR9-19183 Unassigned points in Account Only
                        // AO doesn't have the unassigned territory -1
                        continue;
                    }

                    const territoryExceptions = territoryToRemoveException.ES_EXCEPTIONS.find(e => e.layerMetaId == layerMetaId);

                    // No exceptions for layerMetaId
                    if (!territoryExceptions) {
                        continue;
                    }

                    territoryExceptions.featureIds = _.difference(territoryExceptions.featureIds, [featureId]);
                    // if the territory no longer has exceptions for that layerMetaId, remove it form the exceptions array
                    if(territoryExceptions.featureIds.length === 0) {
                        const index = _.findIndex(territoryToRemoveException.ES_EXCEPTIONS, ['layerMetaId', layerMetaId]);
                        if(index > -1){
                            territoryToRemoveException.ES_EXCEPTIONS.splice(index, 1);
                        }
                    }
                }
            }
        }

        if(!isSuppressEvents && exceptions.length > 0){
            const eventData: ITerritoryActions = {"REMOVE_EXCEPTIONS": exceptions};
            this.eventChannel.fireEvent("onTerritoryChange", eventData, [], this.changedTerritoriesIds);
        }
    }

    private getTerritoryIdByException(layerMetaId: number, featureId: number) {
        for(const territory of this.territoryFeatures) {
            if(territory.ES_EXCEPTIONS.length === 0) {
                continue;
            }

            const exceptionsForLayerMetaId = territory.ES_EXCEPTIONS.find(e => e.layerMetaId === layerMetaId);
            if(!exceptionsForLayerMetaId) {
                continue;
            }

            if(exceptionsForLayerMetaId.featureIds.includes(featureId)) {
                return territory.ES_ID;
            }
        }
        return null;
    }

    /*
     *  -------------------------------------------------------------------------------------------------------------
     *  Assignment public methods
     *  -------------------------------------------------------------------------------------------------------------
     */

    assignBaseFeatures (features:any[], territoryId: number, isSuppressEvents: boolean = false) {
        const territory = this.get(territoryId);
        if (!territory) {
            console.warn("assignPolygonFeatures called with no territory selected. featIds:", features.map(f => f.id));
            return;
        }
        return this.assign("ES_BASE_FEATURE_IDS", features, isSuppressEvents, territory);
    }

    /**
     *
     * See also: #assignPolygonFeatures
     */
    assignPointFeatures (features: any[], territoryId: number, isSuppressEvents: boolean = false) {
        const territory = this.get(territoryId);

        if (!territory) {
            console.warn("assignPointFeatures called with no territory. featIds:", features.map(f => f.id));
            return;
        }

        // Filter out feature already assigned to the territory
        features = features.filter(f => {
            return !(territory as ITerritoryFeature).ES_BASE_POINT_IDS.includes(f.id);
        });

        return this.assign("ES_BASE_POINT_IDS", features, isSuppressEvents, territory);
    }

    unAssignPolygonAndPointFeatures (polygonFeatures: any[], pointFeatures: any[], isSuppressEvents: boolean) {
        this.resetChangedTerritoriesIds();
        const lowerLevelChangedTerritories = new Set();

        if (polygonFeatures?.length) {
            for (const polygonFeature of polygonFeatures) {
                const ter = this.getByBaseFeatureId(polygonFeature.id, this.isHierarchical() ? this.territoryMeta.levels : 1);
                if (ter) {
                    this.unAssignPolygons([polygonFeature], true, ter);
                    this.updateChangedTerritoriesIds(ter.ES_ID);
                    lowerLevelChangedTerritories.add(ter);
                }
            }
        }

        if (pointFeatures?.length) {
            for (const pointFeature of pointFeatures) {
                const ter = this.getByBasePointFeatureId(pointFeature.id);
                if (ter) {
                    this.unAssignPoints([pointFeature], true, ter);
                    this.updateChangedTerritoriesIds(ter.ES_ID);
                    lowerLevelChangedTerritories.add(ter);
                }
            }
        }

        if (!isSuppressEvents) {
            this.eventChannel.fireEvent("onTerritoryChange", {"REMOVE_FEATS": {
                    baseFeatureIds: polygonFeatures.map(f => f.id),
                    basePointIds: pointFeatures.map(f => f.id)}
                }, [...lowerLevelChangedTerritories], this.changedTerritoriesIds);
        }
    }

    assignExceptionFeatures (layerMetaId: number, features: any[], territoryId: number) {
        this.resetChangedTerritoriesIds();

        let territory = this.getCurrentTerritoryObjRef();
        if (territoryId) {
            territory = this.get(territoryId);
        }

        const featureIds = features.map(f => f.id);
        if (!territory) {
            console.warn("assignExceptionFeature called with no territory selected. featIds:", featureIds);
            return;
        }

        if (featureIds.length){
            const exceptions = {
                layerMetaId: layerMetaId,
                featureIds: featureIds
            };
            this.assignExceptions([exceptions], false, territory);
        }
    }

    /**
     * Locks a feature to a particular territory. The exceptions must already be assigned
     * @param layerMetaId
     * @param featureIds
     * @param isSuppressEvents
     */
    lockExceptions  (layerMetaId: number, featureIds: number[], isSuppressEvents= false){

        if(!this.isAccountOnly()){
            console.error("lockExceptions called in a Areas and Accounts territory");
            return;
        }
        this.resetChangedTerritoriesIds();

        // We don't pass the territoryId since the features can be assigned to different territories.
        // We lookup the assigned territory via the territoryPointMapping.
        const featuresByTerritoryId = _.groupBy(featureIds.map(featId => ({territoryId: this.pointMapping.getNode(layerMetaId, featId).territoryId, featId})), "territoryId");
        for(const [territoryId, featuresMapping] of Object.entries(featuresByTerritoryId)) {

            const featIds = featuresMapping.map((m: any )=> m.featId);
                const territory = this.get(+territoryId);
                if (!territory) {
                    console.warn(`Territory id=${territoryId} doesn't exist.`);
                    continue;
                }


                const newLockedExceptions = {layerMetaId, featureIds: featIds};

                if(_.isEmpty(territory.ES_LOCKED_EXCEPTIONS)) {
                    territory.ES_LOCKED_EXCEPTIONS.push(newLockedExceptions);
                } else {
                    // Iterate over all locked exceptions to check if there are already some locked exceptions on the target layerMetaId. If they are update the featureIds array
                    let areAlreadyLockedExceptionsForLayerMeta = false;
                    for(const currentLockedExceptions of territory.ES_LOCKED_EXCEPTIONS as ITerritoryException[]) {
                        if(currentLockedExceptions.layerMetaId === layerMetaId) {
                            areAlreadyLockedExceptionsForLayerMeta = true;
                            currentLockedExceptions.featureIds = _.uniq([...currentLockedExceptions.featureIds, ...featureIds]);
                            break;
                        }
                    }
                    // In case none of the defined locked exceptions are related to the target layerMetaId, simply add it
                    if(!areAlreadyLockedExceptionsForLayerMeta) {
                        (territory.ES_LOCKED_EXCEPTIONS as ITerritoryException[]).push(newLockedExceptions);
                    }
                }
        }

        if(!isSuppressEvents) {
            this.eventChannel.fireEvent("onTerritoryChange", {"LOCK_EXCEPTIONS": {layerMetaId, featureIds}});
        }
    }

    /**
     * unlocks features from their territories. Exceptions must already be assigned
     * @param layerMetaId
     * @param featureIds
     * @param isSuppressEvents
     */
    unlockExceptions (layerMetaId: number, featureIds: number[], isSuppressEvents=false){
        if(!this.isAccountOnly()){
            console.error("lockExceptions called in a Areas and Accounts territory");
            return;
        }

        this.resetChangedTerritoriesIds();

        // We don't pass the territoryId since the features can be assigned to different territories.
        // We lookup the assigned territory via the territoryPointMapping.
        const affectedTerritoryIds = _.uniq(featureIds.map(featId => this.pointMapping.getNode(layerMetaId, featId).territoryId));
        for(const territoryId of affectedTerritoryIds) {
            const territory = this.get(territoryId);
            if (!territory) {
                console.warn("territory with id " + territoryId + " doesn't exist. skipping....");
                continue;
            }

            this.updateChangedTerritoriesIds(territoryId);

            for(const [idx, lockedExceptions] of (territory.ES_LOCKED_EXCEPTIONS as ITerritoryException[]).entries()){
                if(lockedExceptions.layerMetaId === layerMetaId) {
                    lockedExceptions.featureIds = _.difference(lockedExceptions.featureIds, featureIds);
                    if(_.isEmpty(lockedExceptions.featureIds)){
                        (territory.ES_LOCKED_EXCEPTIONS as ITerritoryException[]).splice(idx, 1);
                    }
                    break;
                }
            }
        }

        if(!isSuppressEvents) {
            this.eventChannel.fireEvent("onTerritoryChange", {"UNLOCK_EXCEPTIONS": {layerMetaId, featureIds}});
        }
    }


    /**
     * Remove an point exception from the current territory
     * @param layerMetaId
     * @param feature
     * @param isSuppressEvents
     */
    unAssignExceptionFeature (layerMetaId: number, feature: any, isSuppressEvents:boolean) {
        this.resetChangedTerritoriesIds();

        // If the exception is located in a territory but not directly assigned we need to mark it as changed
        let terr;
        if (this.isAccountOnly()) {
            terr = this.getByExceptionFeatureId(layerMetaId, feature);
        } else {
            const baseFeatureId = this.getFeatureAttrs(feature)[this.getLinkAttributeName()];
            terr = this.getTerritoryByBaseFeatureId(baseFeatureId);
        }

        if(terr) {
            this.updateChangedTerritoriesIds(terr.ES_ID);
            while(terr && terr.ES_PARENT_ID && terr.ES_PARENT_ID > 0){
                this.updateChangedTerritoriesIds(terr.ES_PARENT_ID);
                terr = this.get(terr.ES_PARENT_ID);
            }
        }

        const exception = {
            layerMetaId: layerMetaId,
            featureIds: [feature.id]
        };
        this.unAssignExceptions([exception], isSuppressEvents)
    }

    dropExceptions (exceptions: ITerritoryException[], isSuppressEvents?: boolean) {

        this.unAssignExceptions(exceptions, true);
        // We need this to be empty otherwise it will recalculate the derived attribute and cause an infinite loop
        this.resetChangedTerritoriesIds();

        if(!isSuppressEvents) {
            this.eventChannel.fireEvent("onTerritoryChange", {"DROP_EXCEPTIONS": true});
        }
    }

    /*
    *  -------------------------------------------------------------------------------------------------------------
    *  End assignment  methods
    *  -------------------------------------------------------------------------------------------------------------
    */
    _lastTerIdFound = -1;
    //es._lastTerIdCacheHit = 0;
    //es._lastTerIdCacheMiss = 0;
    private searchTerByFeatId (ter: ITerritoryFeature, featId: number){
        if(es.util.array.binarySearch(ter.ES_BASE_FEATURE_IDS, featId) > -1){
            this._lastTerIdFound = ter.ES_ID;
            return true;
        }
        return false;
    }

    /**
     * This method is called a lot when rendering tile-by-tile.
     * We store and check the last territory that this method found so that we can bias the subsequent searches
     * in favour of that last territory. This saves us having to search through, on average, half the assigned
     * features in order to find the next one we're looking for.
     * see: _lastTerIdFound
     */
    getTerritoryByBaseFeatureId (featureId: number){
        //const featId = Number(featureId);

        if(this._lastTerIdFound){
            const lastTer = this.get(this._lastTerIdFound);
            if(lastTer && this.searchTerByFeatId(lastTer, featureId)){
                //es._lastTerIdCacheHit++;
                return lastTer;
            }
        }
        const territoryFeaturesLength = this.territoryFeatures.length;
        for(let i = 0; i < territoryFeaturesLength; i++){
            if(this._lastTerIdFound != this.territoryFeatures[i].ES_ID && this.searchTerByFeatId(this.territoryFeatures[i], featureId)){
                //es._lastTerIdCacheMiss++;
                return this.territoryFeatures[i];
            }
        }
        return null;
    }

    /**
     * Get the territory containing the specified base feature id.
     * On hierarchical territories return the territory or parent territory corresponding to the specified level.
     * If the base feature is un-assigned at the specified level and is assigned at a higher level, returns the territory it is assigned to.
     *
     * @param featureId Base feature Id to look up.
     * @param level For hierarchical, specify on which level to look up. Use current level if set to null.
     * @returns Territory the base feature is assigned to. On hierarchical, the territory can be on a different than the one specified.
     */
    getByBaseFeatureId (featureId: number, level: number|null = null) {
        //console.warn("TODO: review/refactor getByBaseFeatureId. assumes baseFeatIds may be on upper levels");

        if(featureId === undefined || featureId === null) {
            return null
        }

        if(this.isHierarchical() && !level) {
            level = this.getCurrentLevel();
        }

        const territory = this.getTerritoryByBaseFeatureId(featureId);

        if (territory) {
            // Not hierarchical
            if(!level || territory.ES_LEVEL === level) {
                return territory;
            }

            // If the feature is on a sub-level territory we need to return the parent territory
            let parent = this.get(territory.ES_PARENT_ID as number);
            while(parent) {
                if(parent.ES_LEVEL === level){
                    return parent;
                }
                parent = this.get(parent.ES_PARENT_ID as number);
            }
        }

        // Base feature is un-assigned at the specified level and is assigned at a higher level.
        return territory;
    }

    getByBasePointFeatureId (featureId: number) {
        //featureId = Number(featureId);
        return this.territoryFeatures.find(function(f) {
            if (f.hasOwnProperty("ES_BASE_POINT_IDS")) {
                if (f.ES_BASE_POINT_IDS.length < 256) {
                    return f.ES_BASE_POINT_IDS.includes(featureId);
                } else {
                    return es.util.array.binarySearch(f.ES_BASE_POINT_IDS, featureId) > -1;
                }
            }
        }) || null;
    }

    getByBasePointFeatureIdForLevel (featureId: number, level: number | null) {
        //featureId = Number(featureId);

        if(this.isHierarchical() && !level) {
            level = this.getCurrentLevel();
        }

        const territory = this.getByBasePointFeatureId(featureId);

        if(!territory) {
            return undefined;
        }

        if (territory.ES_LEVEL === level) {
            return territory;
        }

        // If the feature is on a sub-level territory we need to return the parent territory
        let parent = this.get(territory.ES_PARENT_ID as number);
        while(parent) {
            if(parent.ES_LEVEL === level){
                return parent;
            }
            parent = this.get(parent.ES_PARENT_ID as number);
        }

        return territory;
    }
    /**
     * Get the Territory Feature where the given feature is assigned as an exception.
     * @param layerMetaId LayerView Id of the feature
     * @param featureId Feature id
     * @param level Optional, if not set, the current level is used
     * @return TerritoryFeature
     */
    getByExceptionFeatureId (layerMetaId: number, featureId: number, level: number | null = null) {
        const node = this.pointMapping.getNode(layerMetaId, featureId);
        if (node === null || !node.isException) {
            return null;
        }

        if (level && this.getTerritoryMetaObjRef().levels !== level) {
           const territoryIdAtLevel = this.getAncestors(node.territoryId, undefined, level)[0];
           return this.get(territoryIdAtLevel);
        }

        return this.getExtended(node.territoryId);
    }

    /**
     * Get the territory a dataset point feature belongs to at the given level: either
     * assigned directly as an exception, or via its link attribute to a base feature.
     *
     * @param layerMetaId LayerMeta id of the feature's layer
     * @param feature The point feature
     * @param level Territory level to resolve at
     * @returns TerritoryFeature at that level, or null if the feature is unassigned at that level
     */
    getTerritoryForPointFeature (layerMetaId: number, feature: any, level: number | null = null) {
        let terr = this.getByExceptionFeatureId(layerMetaId, feature.id, level);

        if (!terr) {
            terr = this.getByBaseFeatureId(this.getFeatureAttrs(feature)[this.getLinkAttributeName()], level);
        }

        if (terr && terr.ES_LEVEL === level) {
            return terr;
        }

        return null;
    }

    /**
     * Get the Territory Feature where the given feature is assigned as an exception.
     * @param layerMetaId
     * @param featureId
     * @returns {null|*} TerritoryFeature
     */
    getByExceptionFeatureIdNoHierarchy (layerMetaId: number, featureId: number) {
        const featuresToSearch = this.territoryFeatures.slice();
        if(this.unassignedTerritory) {
            featuresToSearch.push(this.unassignedTerritory);
        }

        for (const terFeat of featuresToSearch) {
            if (terFeat.hasOwnProperty('ES_EXCEPTIONS')) {
                const territoryFeatureExceptions = terFeat['ES_EXCEPTIONS']; // exceptions per lm
                for (const exceptions of territoryFeatureExceptions) {
                    if (exceptions['layerMetaId'] === layerMetaId) {
                        const exceptionFeatureIds = exceptions['featureIds'];
                        if (es.util.array.binarySearch(exceptionFeatureIds, featureId) > -1) {
                            return terFeat;
                        }
                    }
                }
            }
        }
        return null;
    }

    setCurrentTerritoryById (territoryId: number|null) {

        if (territoryId == null || territoryId === -1) {
            territoryId = -1;
        } else {
            let isFound = false;
            for(const terFeat of this.territoryFeatures) {
                if (terFeat.ES_ID === territoryId){
                    isFound = true;
                    break;
                }
            }

            if (!isFound) {
                this.currentTerritoryId = -1;
            }
        }

        if (this.currentTerritoryId !== territoryId) {
            this.currentTerritoryId = territoryId;
            this.eventChannel.fireEvent("onTerritoryCurrentChange", this.currentTerritoryId);
        }
    }

    /**
     * NOTE: this returns modifiable territory obj
     */
    getCurrentTerritoryObjRef (){
        if(this.currentTerritoryId < 0){
            return null;
        }
        return this.get(this.currentTerritoryId);
    }

    getBaseLayerConfigObjRef (){
        return this.baseLayerConfig;
    }

    getBaseLayerConfigClone (){
        return es.util.object.deepCopy(this.baseLayerConfig);
    }

    getTerritoryFeaturesClone (){
        return es.util.object.deepCopy(this.territoryFeatures);
    }

    /**
     * Get territoryFeatures obj by reference.
     * Warning: use with extreme caution. Any edits made directly to the model are will likely cause corruption or behaviour bugs.
     * This should only be used for performance reasons when the the model will be cloned anyway e.g. for persistence by undo/redo
     * @return territoryFeatures object ref
     */
    getTerritoryFeaturesObjRef (){
        return this.territoryFeatures;
    }

    getNumTerritoryFeatures (){
        return this.territoryFeatures.length;
    }

    getTerritoryFeaturesToSave () {
        let features = this.getTerritoryFeaturesClone();
        if(this.isAccountOnly()){
            return features;
        }

        // add unassignedTerritory to list of features to save to DB
        const unassignedTerritory = es.util.object.deepCopy(this.unassignedTerritory);
        unassignedTerritory.ES_BASE_FEATURE_IDS = [];
        features.unshift(unassignedTerritory);
        features.unshift(...this.unassignedTerritoryHierarchy);
        return features;
    }

    /**
     * Get baseFeatIds by territory id
     * @param territoryIds single territory id or array of ids
     */
    getBaseFeatureIds(territoryIds: number[] | number): number[] {
        if (this.isAccountOnly()) {
            return [];
        }
        if (!Array.isArray(territoryIds)) {
            territoryIds = [territoryIds];
        }
        const baseFeatIds: number[] = [];
        for (const terId of territoryIds) {
            const territory = this.get(terId);
            baseFeatIds.push(...territory.ES_BASE_FEATURE_IDS);
            const childTers = this.getByParentId(territory.ES_ID);
            for (const childTer of childTers) {
                baseFeatIds.push(...childTer.ES_BASE_FEATURE_IDS);
            }
        }
        baseFeatIds.sort((a, b) => a - b);
        return baseFeatIds;
    }


    /**
     * Get basefeatids by territory id
     * @param territoryIds single territory id or array of ids
     * @returns {[]|*[]}
     */
    getBasePointFeatureIds (territoryIds: number[] | number) {
        if(!Array.isArray(territoryIds)){
            territoryIds = [territoryIds];
        }
        let baseFeatIds: number[] = []
        for(const terId of territoryIds) {
            const territory = this.get(terId);
            baseFeatIds = _.union(baseFeatIds, territory.ES_BASE_POINT_IDS);
            if (!baseFeatIds) {
                return [];
            }
            const childTers = this.getByParentId(territory.ES_ID);
            for (const childTer of childTers) {
                const childBaseFeatureIds = childTer.ES_BASE_POINT_IDS;
                baseFeatIds = _.union(baseFeatIds, childBaseFeatureIds);
            }
        }
        return baseFeatIds;
    }

    /**
     * Get exception for the current territory and its children
     * @param territoryId
     * @returns Exceptions
     */
    getExceptions (territoryId: number) {

        const territory = this.get(territoryId);
        let exceptions = territory.ES_EXCEPTIONS || [];
        const childTers = this.getByParentId(territory.ES_ID);

        for (const childTer of childTers) {
            const childTerExceptions = childTer.ES_EXCEPTIONS;
            exceptions = this.concatExceptions(exceptions, childTerExceptions);
        }
        return exceptions;
    }

    isHierarchical () {
        return this.hasTerritory() && this.territoryMeta.levels != null;
    }

    isAccountOnly () {
        return this.hasTerritory() && this.territoryMeta.type === "ACCOUNTS_ONLY";
    }

    isImportedTerritory() {
        return this.hasTerritory() && this.territoryMeta.importAttributes && this.territoryMeta.importAttributes.length > 0;
    }

    /**
     * Checks if we are working on the bottom level (or if the Territory is not Hierarchical)
     * @returns {boolean}
     */
    isFlatOrOnBottomLevel  (){
        return !this.isHierarchical() || this.getCurrentLevel() === this.getTerritoryMetaObjRef().levels;
    }

    /**
     * Check if a given territory can be removed.
     * @param id Territory Id
     * @returns {[[boolean, [*]]]} An array where the first element is the value of the flag isRemovable and the second element is a list of the territories that are preventing the deletion.
     */
    canRemoveTerritory  (id: number) {
        const territory = this.get(id);

        if(!_.isEmpty(territory.ES_LOCKED_EXCEPTIONS)) {
            return [false, [territory]];
        }

       const lowestLevelTerritoriesWithLockedAccounts = this.getByParentId(id, true).filter(territory => !_.isEmpty(territory.ES_LOCKED_EXCEPTIONS));
        return [_.isEmpty(lowestLevelTerritoriesWithLockedAccounts), lowestLevelTerritoriesWithLockedAccounts];
    }

    /**
     * Check if territoryA is a parent of territoryB
     */
    isParentOf (territoryA: ITerritoryFeature, territoryB: ITerritoryFeature) {

        if(!this.isHierarchical() || territoryA.ES_ID === territoryB.ES_ID) {
            return false;
        }

        if((territoryB.ES_LEVEL as number) <= (territoryA.ES_LEVEL as number)) {
            return false;
        }

        if(territoryA.ES_ID === territoryB.ES_PARENT_ID) {
            return true;
        }

        let child = territoryB;
        while (child?.ES_PARENT_ID && child.ES_PARENT_ID > 0) {
            if(child.ES_PARENT_ID === territoryA.ES_ID) {
                return true;
            }
            child = this.get(child.ES_PARENT_ID);
        }
    }

    getCurrentLevel () {
        return this.isHierarchical() ? this.currentLevel : null;
    }

    getNewTerritoryLabel () {
        if (this.isHierarchical()) {
            return this.getTerritoryLevelLabel(this.currentLevel, true);
        }
        return "Territory";
    }

    getTerritoryLabel () {
        if (this.isHierarchical()) {
            return this.getTerritoryLevelLabel(this.currentLevel + 1, false);
        }
        return "Territory";
    }


    isLowestLevel () {
        return !this.isHierarchical() || this.territoryMeta.levels === this.currentLevel;
    }

    isBottomLevel (lvl: number) {
        lvl = lvl || this.currentLevel;
        return !this.isHierarchical() || (this.isHierarchical() && lvl == this.territoryMeta.levels);
    }

    private saveLevelToCriteria(newLevel: number) {
        return new Promise<void>( (resolve) => {
            let criteria: Criteria = this.mainLayerView.getCriteriaClone();
            let levelCriteria : EqualCriteria<number> = es.util.criteria.find(criteria, {'$type': 'Equal', 'attr': 'ES_LEVEL'});

            if (levelCriteria) { // there should be every time, just in case...
                levelCriteria.value = newLevel; // little optimization, as find returns reference
            } else {
                levelCriteria = {
                    '$type': 'Equal',
                    'attr': 'ES_LEVEL',
                    'value': newLevel
                };
                criteria = es.util.criteria.and(criteria, levelCriteria);
            }
            this.mainLayerView.setCriteria(criteria);
            resolve();
        });
    }

    async setCurrentLevel (newLevel: number, isSuppressEvents=false) {

        if (newLevel !== this.currentLevel) {
            this.currentLevel = newLevel;

            // Set "All territories" select when changing level
            this.setCurrentParent(null, true);
            this.setCurrentTerritoryById(-1);

            // await criteria save + listeners so callers (esp.  mapbook export) can await setCurrentLevel
            await this.saveLevelToCriteria(newLevel);
            if (!isSuppressEvents) {
                await this.eventChannel.fireEvent("onTerritoryLevelChange", this.currentLevel, newLevel);
            }
            this.selectionModel.reset();
        }
    }

    fixLevel (newLevel: number) {
        // Set "All territories" select when changing level
        //this.setCurrentParent(null, true);
        this.setCurrentTerritoryById(-1);

        this.saveLevelToCriteria(newLevel).then(() => {
            // grabbing from server, just to overwrite criteria... should simply save full layerview?!
            es.api.layerView.get(this.mainLayerView.getId()).done((lv: any) => {
                lv.criteria = this.mainLayerView.getCriteriaObjRef();
                es.api.layerView.update(lv, undefined, true);
            });
        });

    }

    getCurrentParent (){
        return this.parent;
    }

    /**
     * Return list of "active" (i.e. visible and non-filtered) territories
     * @param isUseFilter
     * @param isUseLevel
     * @returns terFeats[]
     */
    getActive  (isUseFilter: boolean, isUseLevel: boolean){
        let terFeats = [];

        if (isUseLevel && this.isHierarchical()){
            const level = this.getCurrentLevel() as number;
            terFeats = this.getTerritoryFeaturesByLevelObjRef(level);
        } else {
            terFeats = this.getTerritoryFeaturesObjRef();
        }

        if (isUseFilter) {
            const filteredTerFeatIds = this.getFilter();
            if (filteredTerFeatIds) {
                terFeats = _.filter(terFeats, function (terFeat) {
                    return _.indexOf(filteredTerFeatIds, terFeat.ES_ID) !== -1;
                });
            }
        }

        return terFeats;
    }

    setCurrentParent (newParent: ITerritoryFeature | null, isSuppressEvents: any) {

        if (newParent != this.parent) {

            this.parent = newParent;
            this.setCurrentTerritoryById(-1);

            if(!isSuppressEvents) {
                this.eventChannel.fireEvent("onTerritoryParentChange", this.parent, newParent);
            }
        }
    }


    /**
     * Get filtered territories
     * @return List of Ids of territories that are visible
     */
    getFilter (){
        if(!this.mainLayerView){
            return null;
        }
        const criteria: Criteria = this.mainLayerView.getCriteriaObjRef();
        const filterCriteria: null | InCriteria<number> = es.util.criteria.find(criteria, {'$type': 'In', 'attr': 'ES_ID'});
        return filterCriteria ? filterCriteria.values : null;
    }

    /**
     * Set the current filter
     * @param territoriesIds Ids of territories that are visible
     * @param isSuppressEvents
     */
    async setFilter (territoriesIds: number[] | null, isSuppressEvents=false){
        let criteria : Criteria  = this.mainLayerView.getCriteriaClone();
        let filterCriteria : null | InCriteria<number> = es.util.criteria.find(criteria, {'$type': 'In', 'attr': 'ES_ID'});

        if (!territoriesIds) {
            if (filterCriteria) {
                criteria = es.util.criteria.remove(criteria, {'$type': 'In', 'attr': 'ES_ID'})
            }
        } else {
            const values = territoriesIds.length === 0 ? [-1] : territoriesIds;
            if (filterCriteria) {
                filterCriteria.values = values;
            } else {
                filterCriteria = {
                    '$type': 'In',
                    'attr': 'ES_ID',
                    'values': values
                };
                criteria = es.util.criteria.and(criteria, filterCriteria);
            }
        }
        await this.mainLayerView.updateLayerView({'criteria': criteria});
        if(!territoriesIds || !territoriesIds.includes(this.currentTerritoryId)){
            this.currentTerritoryId = -1;
        }

        if (!isSuppressEvents) {
            // await listeners so setFilter resolves only after layer updates have actually been applied to Mapbox
            await this.eventChannel.fireEvent("onTerritoryFilterChange", "CHANGE_FILTER", territoriesIds);
        }
    }

    getTerritoryFeaturesByLevelObjRef(level: number): ITerritoryFeature[] {
        return this.territoryFeatures.filter(function (t) {
            return t.hasOwnProperty("ES_LEVEL") && t.ES_LEVEL === level;
        });
    }

    getByParentId (parentId: number, recursively=true) {
        const children = this.territoryFeatures.filter(f => f.ES_PARENT_ID == parentId);
        let descendents: ITerritoryFeature[] = [];
        if(recursively) {
            for (const child of children) {
                descendents.push(...this.getByParentId(child.ES_ID));
            }
        }
        descendents = descendents.concat(children);
        return descendents;
    }

    getAncestors (territoryId: number, attribute = "ES_ID", level: number | null = null): number[]{
        if(!this.territoryMeta.levels) {
            return [];
        }
        const ancestors: number[] = [];
        if (territoryId < 0) {
            let currentTerritory = this.getUnassignedTerritory();
            if (territoryId < -1) {
                currentTerritory = _.find(this.unassignedTerritoryHierarchy, value => value.ES_ID == territoryId);
            }
            ancestors.push(currentTerritory[attribute] || "");
            while (currentTerritory?.ES_PARENT_ID) {
                currentTerritory = _.find(this.unassignedTerritoryHierarchy, value => value.ES_ID == currentTerritory.ES_PARENT_ID);
                ancestors.push(currentTerritory[attribute] || "");
            }
            return ancestors;
        }
        attribute = attribute || "ES_ID";

        let territory = this.get(territoryId);

        if( level !== undefined && territory.ES_LEVEL === level) {
            return [territory[attribute]];
        }

        while (territory.ES_PARENT_ID && territory.ES_PARENT_ID > 0){
            territory = this.get(territory.ES_PARENT_ID);
            if (!territory) break;
            ancestors.unshift(territory[attribute]);
            if(level !== undefined && territory.ES_LEVEL === level){
                break;
            }
        }
        return ancestors;
    }

    getLinkAttributeName () {
        if(this.territoryMeta == undefined){
            console.warn("getLinkAttributeName called before territoryMeta has loaded!", this.territoryMeta);
            return null;
        }
        return this.territoryMeta['linkAttributeName'][this.baseLayerFsId];
    }

    getExtraPointLinkAttributeName (){
        // TODO Not sure this should be hard-coded
        // problem with using non-static attribute name is the link ES_LINK_FSID_ES_ID where ES_LINK is constant FSID
        // is featureSourceId and ES_ID is the linked feature source attribute used to link
        // this value may not be know in advance, need more investigation and we will see how easy/hard it will be now
        // create new territory layer view configurations when moved to database
        return "ES_DEFAULT_US_ZCTA_ID";
    }

    getChangedTerritoriesIds (){
        return this.changedTerritoriesIds;
    }

    resetChangedTerritoriesIds (){
        this.changedTerritoriesIds = [];
    }

    private updateChangedTerritoriesIds (territoryId: number){
        if(territoryId < 0){
            return;
        }

        if(!this.changedTerritoriesIds.includes(territoryId)) {

            this.changedTerritoriesIds.push(territoryId);

            if (this.isHierarchical()){
                this.changedTerritoriesIds = _.union(this.changedTerritoriesIds, this.getAncestors(territoryId));
            }
        }
    }

    setTerritoryFeatureName (id: number , name: string, externalId: (string|null) = null){
        const territory = this.territoryFeatures.find(t => t.ES_ID == id);
        if (territory && name != territory.NAME){
            territory.NAME = this.getValidTerritoryName(name);
            if (externalId !== null) {
                territory.ES_EXTERNAL_ID = externalId;
            }
            this.eventChannel.fireEvent("onTerritoryChange", {"CHANGE_NAME": true}, [territory]);
        }
    }

    setTerritoryFeatureColor (ids: number[] , colors: number[] | number){

        // mode determines if the color was changed via the reset colors or via the color picker
        // this is used to not trigger a re-render on the territory items table on the right side and close the color picker
        const mode = Array.isArray(ids) ? "multiple" : "single";
        if(!Array.isArray(ids)) {
            ids = [ids];
            colors = [colors as number];
        }

        const changedTerritories = []
        for(let i = 0; i < ids.length; i++){
            const territory = this.territoryFeatures.find(t => t.ES_ID == ids[i]);
            if(!territory){
                console.warn("No territory found with id=", ids[i]);
                continue;
            }
            territory.ES_STYLE_COLOR = (colors as number[])[i];
            changedTerritories.push(territory);
        }
        this.eventChannel.fireEvent("onTerritoryChange", {"CHANGE_COLOR": mode }, changedTerritories);
    }

    setTerritoryFeatureAttribute (id:number , attrName: string, value: any) {
        let attrs = this.featureViewModel.getLayerById(this.mainLayerView.getId()).getLayerInfo().attributes;
        attrs = attrs.filter((a: any) => {
            return a.name != 'NAME'
                && this.territoryMeta.territoryDerivedAttributes.findIndex(function(tda: any) { return tda.name == a.name; }) == -1;
        });

        if (attrs.findIndex(function(a: any) { return a.name == attrName; }) == -1) {
            throw new Error("Invalid attribute name [" + attrName + "]");
        }

        const territory = this.territoryFeatures.find(t => t.ES_ID == id);
        if (territory) {
            territory[attrName] = value;
            this.eventChannel.fireEvent("onTerritoryChange", {"CHANGE_ATTRIBUTE": true}, [territory]);
        }
    }

    getTerritoryFeatureAttribute (id: number , attrName: string) {

        const territory = this.territoryFeatures.find(t => t.ES_ID == id);

        if (!territory) {
            throw new Error("Invalid territory id [" + id + "]");
        }

        const attrs = this.featureViewModel.getLayerById(this.mainLayerView.getId()).getLayerInfo().attributes;
        if (attrs.findIndex(function(a: any) { return a.name == attrName; }) == -1) {
            throw new Error("Invalid attribute name [" + attrName + "]");
        }

        return territory[attrName];
    }

    removeTerritoryAttribute(attr: any) {
        for (const terFeat of this.territoryFeatures) {
            if (terFeat.hasOwnProperty(attr)) {
                delete terFeat[attr];
            }
        }
        this.eventChannel.fireEvent("onTerritoryAttributeChange", "DELETE", attr);
    }

    /**
     * Move a territory feature to a new parent
     * @param territoryId Id of the territory to move
     * @param newParentId Id of the new parent
     */
    moveTerritory (territoryId: number, newParentId: number) {
        this.resetChangedTerritoriesIds();
        if(!this.get(territoryId) || !this.get(newParentId) || !this.get(this.get(territoryId).ES_PARENT_ID as number)) {
            throw new Error("Failed to move territory, invalid territory and/or parent");
        }

        const territoryToMove = this.get(territoryId);
        const oldParentTerritory = this.get(territoryToMove.ES_PARENT_ID as number);
        const newParentTerritory = this.get(newParentId);

        if(oldParentTerritory.ES_LEVEL && newParentTerritory.ES_LEVEL && oldParentTerritory.ES_LEVEL !== newParentTerritory.ES_LEVEL) {
            throw new Error("The level of the destination territory must be the same as the origin");
        } else {
            territoryToMove.ES_PARENT_ID = newParentId;
            this.updateChangedTerritoriesIds(oldParentTerritory.ES_ID);
            this.updateChangedTerritoriesIds(newParentTerritory.ES_ID);
            this.updateChangedTerritoriesIds(territoryToMove.ES_ID);
            this.eventChannel.fireEvent("onTerritoryChange", {"MOVE": true}, [territoryToMove], this.changedTerritoriesIds);
        }
    }

    /**
     * Move an array of territory features to a new parent
     * @param territories Territories to move, must be on the same level.
     * @param newParentId Id of the new parent
     */
    moveTerritories (territories: ITerritoryFeature[], newParentId: number) {
        this.resetChangedTerritoriesIds();

        if(territories.length == 0 || !this.get(newParentId)) {
            throw new Error("Failed to move territories, invalid territory and/or parent");
        }

        const oldParentTerritoryIds = territories.map(t => t.ES_PARENT_ID)
        const oldParentTerritories = this.territoryFeatures.filter(t => oldParentTerritoryIds.includes(t.ES_ID));
        const oldLevels = _.uniq(oldParentTerritories.map(t => t.ES_LEVEL));
        const newParentTerritory = this.get(newParentId);

        if (oldLevels.length > 1) {
            throw new Error("Territories to move must be on the same level");
        }

        const oldLevel = oldLevels[0];
        if (oldLevel && newParentTerritory.ES_LEVEL && oldLevel !== newParentTerritory.ES_LEVEL) {
            throw new Error("The level of the destination territory must be the same as the origin");
        }

        territories.forEach(t => {
            const territoryToMove = this.get(t.ES_ID);
            territoryToMove.ES_PARENT_ID = newParentId;
            this.updateChangedTerritoriesIds(t.ES_ID);
        });

        oldParentTerritories.forEach(t => this.updateChangedTerritoriesIds(t.ES_ID));

        this.updateChangedTerritoriesIds(newParentTerritory.ES_ID);

        this.eventChannel.fireEvent("onTerritoryChange", {"MOVE": true}, territories, [...oldParentTerritoryIds, newParentId]);
    }

    hasTerritory() {
        return !!this.territoryMeta;
    }

    /**
     * Convenience method to get ManageTerritoryMainLayerView
     */
    getManageTerritoryMainLayerView (){
        return this.mainLayerView;
    }

    /**
     * Convenience method to get ManageTerritoryBaseLayerView
     */
    getManageTerritoryBaseLayerView (){
        return this.baseLayerView;
    }

    /**
     * Return layerviews which should be rendered with geojson.
     * Note: this is intentionally very territory specific for now. We will probably make this rendering method generic
     * in the future, at which point we'll remove this method and probably use some layerview or styling property to
     * indicate rendering method.
     */
    getRelatedLayerViewMapping() {
        if(!this.relatedLayerViewMapping) {
            throw new Error("Trying to access RelatedLayerViewMapping before it's initialized!");
        }
        return this.relatedLayerViewMapping;
    }

    getRelatedFeaturesMapping() {
        if (!this.relatedFeaturesMapping) {
            throw new Error("Trying to access RelatedFeaturesMapping before it's initialized!");
        }
        return this.relatedFeaturesMapping;
    }

    private validateLayerViewIdParam(layerViewId: number){
        if(isNaN(layerViewId) || layerViewId < 0){
            throw new Error("Invalid layerViewId param passed: " + JSON.stringify(layerViewId));
        }
    }

    isBaseLayer (layerViewId: number){
        this.validateLayerViewIdParam(layerViewId);
        return  this.relatedLayerViewMapping?.baseLayerView?.hasOwnProperty(layerViewId) ?? false;
    }

    isRelatedLayer (layerViewId:number){
        this.validateLayerViewIdParam(layerViewId);
        return this.relatedLayerViewMapping?.relatedLayerView?.hasOwnProperty(layerViewId) ?? false;
    }

    isRelatedExtraPointLayer (layerViewId: number){
        this.validateLayerViewIdParam(layerViewId);
        return this.relatedLayerViewMapping?.extraRelatedPointLayerView?.hasOwnProperty(layerViewId) ?? false;
    }

    // Convenience/code readability functions for getRelatedLayerViewMapping
    isBaseOrRelatedLayer (layerViewId: number){
        this.validateLayerViewIdParam(layerViewId);
        return (this.relatedLayerViewMapping?.baseLayerView?.hasOwnProperty(layerViewId) || this.relatedLayerViewMapping?.relatedLayerView?.hasOwnProperty(layerViewId)) ?? false
    }

    isBaseOrRelatedOrExtraPointLayer (layerViewId: number){
        this.validateLayerViewIdParam(layerViewId);
        return this.isBaseOrRelatedLayer(layerViewId) || this.isRelatedExtraPointLayer(layerViewId);
    }

    isBaseOrExtraPointLayer (layerViewId: number){
        this.validateLayerViewIdParam(layerViewId);
        return this.isBaseLayer(layerViewId) || this.isRelatedExtraPointLayer(layerViewId);
    }

    isOutlinesLayer  (layerViewId: number){
        this.validateLayerViewIdParam(layerViewId);
        return this.isHierarchical() && this.getManageTerritoryMainLayerView().getId() === layerViewId;
    }

    hasRelatedExtraPointLayer (){
        return this.relatedLayerViewMapping &&
            _.keys(this.relatedLayerViewMapping?.extraRelatedPointLayerView ?? {}).length > 0;
    }

    getTerritoryMetaClone (){
        return es.util.object.deepCopy(this.territoryMeta);
    }

    /**
     * NOTE: this returns modifiable territory obj
     * @returns {*}
     */
    getTerritoryMetaObjRef (){
        return this.territoryMeta;
    }

    updateDerivedAttributes (newTerritoryDerivedAttributes: any){
        this.territoryMeta.territoryDerivedAttributes = newTerritoryDerivedAttributes;
    }

    getPointMapping () {
        return this.pointMapping;
    }

    isGapFillingEnabled () : boolean {
        return !this.isAccountOnly() && (!this.isHierarchical() || this.getCurrentLevel() === this.territoryMeta.levels);
    }

    isBottomLevel () : boolean {
        return !this.isHierarchical() || this.getCurrentLevel() === this.territoryMeta.levels;
    }

    setFilteredInFeatureIds (layerMetaId: number, featureIds: number[]) : void {
        this.filteredInFeatureIds[layerMetaId] = featureIds;
        
        if (this.featureViewModel.getLayerByLayerMetaId(layerMetaId).getGeomType() == 'POINT') {
            this.pointMapping.recalculateVisibility(layerMetaId, this.filteredInFeatureIds[layerMetaId], this.getFilter());
        }
    }

    getFilteredInFeatureIds (layerMetaId: number) : number[] {
        return this.filteredInFeatureIds[layerMetaId];
    }

    getFilteredInIds () {
        return this.filteredInFeatureIds;
    }

    private onEditTerritoryAttribute(_event: any, editAttrResults: any[]){
        for (const result of editAttrResults) {
            if(result.$type === "RemoveAttributeResult") {
                const attr = result['oldAttribute']['name'];
                this.removeTerritoryAttribute(attr);
            }
        }
    }

    private onEditTerritoryLevelLabels(newTerritoryMeta: any) {
        this.territoryMeta = es.util.object.deepCopy(newTerritoryMeta);
    }

    private onRemoveLayer(lvm: any, _isTransient: boolean, _eventNames: any) {
        // Remove Exceptions for this layer
        const layerMetaId = lvm.getLayerMetaId();
        for(const ter of this.territoryFeatures) {
            if (ter.hasOwnProperty("ES_EXCEPTIONS")) {
                const index = _.findIndex(ter.ES_EXCEPTIONS, ['layerMetaId', layerMetaId]);
                if (index > -1) {
                    ter.ES_EXCEPTIONS.splice(index, 1);
                }
            }
        }
    }

    private loadLevel() {
        if (this.isHierarchical()) {
            const manageTerritoryMainLayerViewCriteria: Criteria = this.mainLayerView.getCriteriaObjRef();
            const levelCriteria: null | EqualCriteria<number> = es.util.criteria.find(manageTerritoryMainLayerViewCriteria, {
                '$type': 'Equal',
                'attr': 'ES_LEVEL'
            });
            let fixLevelCriteria = false;

            if(levelCriteria) {
                this.currentLevel = levelCriteria.value;
            }

            if (this.currentLevel < 1 || this.currentLevel > this.territoryMeta.levels) {
                this.currentLevel = 1;
                fixLevelCriteria = true;
            }
            if (fixLevelCriteria) {
                console.log("Fixed wrong level. ", manageTerritoryMainLayerViewCriteria);
                this.fixLevel(this.currentLevel);
            }
        }
    }

    private createUnassignedTerritory(): ITerritoryFeature {
        const territory =  {
            NAME: $i18n("Unassigned"),
            ES_EXCEPTIONS: [],
            ES_BASE_FEATURE_IDS:[],
            ES_LOCKED_EXCEPTIONS: [],
            ES_GEOM_STATUS_CODE: "TO_BE_SET",
            ES_ID: -1,
            ES_STYLE_COLOR: 0xFFFFFF,
        } as ITerritoryFeature;

        if (this.hasRelatedExtraPointLayer()) {
            territory.ES_BASE_POINT_IDS = [];
        }
        return territory;
    }

    private createUnassignedTerritoryHierarchy(nLevels: number){
        const hierarchy: ITerritoryFeature[] = [];
        for (let i = nLevels, level = 1 ; i > 1; i--, level++) {
            const territory: ITerritoryFeature = {
                NAME: $i18n("Unassigned"),
                ES_EXCEPTIONS: [],
                ES_LOCKED_EXCEPTIONS: [],
                ES_BASE_FEATURE_IDS: [],
                ES_GEOM_STATUS_CODE: "TO_BE_SET",
                ES_ID: -i,
                ES_PARENT_ID: i === nLevels ? undefined:  -i - 1,
                ES_LEVEL: level,
                ES_STYLE_COLOR: 0xFFFFFF
            };
            if (this.hasRelatedExtraPointLayer()) {
                territory.ES_BASE_POINT_IDS = [];
            }
            hierarchy.push(territory);
        }
        return hierarchy;
    }

    private initMeta(geoMapId: number) {
        return es.api.territory.getTerritoryMetaByGeoMapId(geoMapId).then((territoryMetaRs : any) => {
            this.territoryMeta = territoryMetaRs;

            let loadDfd;

            if (this.isAccountOnly()) {
                loadDfd = $.when(es.api.territory.loadAll(this.mainLayerView.getId())).then((territoryFeaturesRs : ITerritoryFeature[]) => {
                    return es.util.deferred.resolved(null, {}, territoryFeaturesRs);
                });
            }
            else {
                loadDfd = es.api.layerMeta.get(this.baseLayerView.getLayerView().layerMetaId).then((baseLayerMeta: any) => {
                    this.baseLayerFsId = baseLayerMeta.featureSourceId;
                    return $.when(
                        es.api.territory.getBaseLayersConfig(baseLayerMeta.parentId),
                        es.api.territory.getRelatedLayerViewMapping(geoMapId),
                        es.api.territory.loadAll(this.mainLayerView.getId()),
                        es.api.feature.listLean(this.baseLayerView.getId(), [])
                    )
                });
            }
            return loadDfd.then(async (baseLayersConfigRs: any, relatedLayerViewMappingRs: any, territoryFeaturesRs: ITerritoryFeature[], baseLayerFeatures: any[]|undefined) =>{
                this.baseLayerConfig = baseLayersConfigRs;
                this.relatedLayerViewMapping = relatedLayerViewMappingRs;
                if(baseLayerFeatures){
                    this.allBaseFeatureIds = baseLayerFeatures.map(f => f.id);
                }

                if (!this.isAccountOnly()) {
                    this.relatedFeaturesMapping = new TerritoryRelatedFeaturesMapping(this.featureViewModel, relatedLayerViewMappingRs);
                } 

                this.unassignedTerritory = this.createUnassignedTerritory();
                if(this.territoryMeta.levels){
                    this.unassignedTerritoryHierarchy = this.createUnassignedTerritoryHierarchy(this.territoryMeta.levels ?? 1);
                    this.unassignedTerritory.ES_LEVEL = this.territoryMeta.levels;
                    this.unassignedTerritory.ES_PARENT_ID = -2;
                }

                if (territoryFeaturesRs && territoryFeaturesRs.length > 0) {
                    this.load(territoryFeaturesRs, -1, true);
                } else {
                    if (this.isHierarchical()) {
                        this.createBlankHierarchy(true);
                    } else {
                        await this.createBlank("Territory 1", true);
                    }
                }


                this.loadLevel();

                this.mainLayerView.getEventChannel().addListener("onUpdate", (layer: any, isTransient: boolean, updateEventNames: any[]) => {
                    if (isTransient || layer.getId() != this.mainLayerView.getId() || !updateEventNames.includes('onSetCriteria')) {
                        return;
                    }

                    this.loadLevel();
                });

                this.eventChannel.addListener("onRemoveLayer", this.onRemoveLayer, this);

                this.pointMapping.init();

                es.api.getEventChannel().addListener("onEditTerritoryAttribute", this.onEditTerritoryAttribute, this);
                es.api.getEventChannel().addListener("onEditTerritoryLevelLabels", this.onEditTerritoryLevelLabels, this);

                this.selectionModel.setSelectionType(this.territoryMeta.type);

                return this.pointMapping.loadMapping().then(() => es.util.deferred.resolved());
            }).fail(function(){
                throw new Error("Failed to initialize territoryModel");
            });
        });
    }
}

/**
 * Sample model obj of territory feature obj:
 *  {
 *      "ES_STYLE_COLOR": 345426,
 *      "ES_BASE_FEATURE_IDS": [22, 23, 35],
 *      "ES_BASE_POINT_IDS": [1, 2],
 *      "ES_EXCEPTIONS": [{layerMetaId: id, featureIds: []},..],
 *      "NAME": "west coast",
 *      "ES_ID": 2,
 *      "ES_STYLE_COLOR": 2,
 *      "TER_Count"
 *  }
 */
export interface ITerritoryFeature {
    ES_ID: number;
    NAME: string;
    ES_LEVEL?: number;
    ES_PARENT_ID?: number;
    ES_STYLE_COLOR: number;
    ES_GEOM_STATUS_CODE: "TO_BE_SET" | "OK" | any; // FIXME :: add the other possible strings
    ES_EXCEPTIONS: ITerritoryException[];
    ES_BASE_POINT_IDS?: number[];
    ES_BASE_FEATURE_IDS: number[]
    ES_LOCKED_EXCEPTIONS?: ITerritoryException[];
    ES_NUM_CHILDREN?: number // Only used for the Span of Control functionality TOR9-17324
    [key: string]: any
}

export interface ITerritoryException{
    layerMetaId: number;
    featureIds: number[];
}

export interface ITerritoryActions  {
    "UNDO"?: true,
    "REDO"?: true,
    "RELOAD"?: true,
    "ADD_TERRITORY"?: true,
    "REMOVE_TERRITORY"?: true,
    "LOCK_EXCEPTIONS"?: {layerMetaId: number, featureIds: number[]},
    "UNLOCK_EXCEPTIONS"?: {[layerMetaId: number]: number[]},
    "ADD_EXCEPTIONS"?: {[territoryId: number]: ITerritoryException[]},
    "REMOVE_EXCEPTIONS"?: ITerritoryException[],
    "DROP_EXCEPTIONS"?: true,
    "CHANGE_NAME"?: true,
    "CHANGE_ATTRIBUTE"?: true,
    "MOVE"?: true,
    "CHANGE_COLOR"?: "multiple" | "single"
    "REMOVE_FEATS"? : {
        baseFeatureIds: number[],
        basePointIds: number[]
    },
    "ADD_FEATS"?: {
        baseFeatureIds: number[],
        basePointIds: number[]
    },
    "SELECT_FEATS"?: true
}
